import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { downloadFile } from "@/pages/_hr/download";
import { DownloadIcon, XIcon } from "@phosphor-icons/react";

const TIPOS = [
  { k: "planilla", l: "Planilla por período y depto." },
  { k: "costo-area", l: "Costo de planilla por área" },
  { k: "ausencias", l: "Ausencias por empleado" },
  { k: "vacaciones", l: "Saldos de vacaciones" },
  { k: "actas", l: "Reconocimientos / amonestaciones" },
  { k: "rotacion", l: "Altas y bajas (rotación)" },
];
const MONEY = new Set(["devengado", "deducciones", "neto", "cargas_patronales", "costo_total", "costo_real", "viaticos_reembolsados"]);

// Reportes RRHH (13.5) con export a Excel.
export default function HrReportesTab() {
  const [tipo, setTipo] = useState("planilla");
  const [f, setF] = useState({ desde: "", hasta: "", departamento: "" });
  const [periods, setPeriods] = useState([]);
  const [selPeriods, setSelPeriods] = useState([]);
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/hr/payroll/periods").then(({ data }) => setPeriods(data || [])).catch(() => {}); }, []);
  const params = () => {
    const p = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    if (selPeriods.length && (tipo === "planilla" || tipo === "costo-area")) p.period_ids = selPeriods.join(",");
    return p;
  };
  const load = async () => {
    try { const { data } = await api.get(`/hr/reportes/${tipo}`, { params: params() }); setData(data); }
    catch (e) { setData(null); toast.error(apiErr(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tipo, f, selPeriods]);
  const usesPeriods = tipo === "planilla" || tipo === "costo-area";
  return (
    <div className="space-y-4" data-testid="hr-reportes">
      <div className="flex flex-wrap gap-1">
        {TIPOS.map((t) => (
          <button key={t.k} onClick={() => setTipo(t.k)} data-testid={`hrrep-${t.k}`}
            className={`text-xs px-3 py-1.5 rounded border ${tipo === t.k ? "bg-agro-green/10 text-agro-green border-agro-green/30" : "border-border text-text-muted"}`}>{t.l}</button>
        ))}
      </div>
      <div className="bg-card border border-border/60 rounded-lg p-3 flex flex-wrap gap-2 items-end text-xs">
        {tipo !== "vacaciones" && <>
          <div><div className="text-[9px] uppercase text-text-muted">Desde</div><input type="date" value={f.desde} onChange={(e) => setF({ ...f, desde: e.target.value })} className="bg-app border border-border rounded px-2 py-1.5" /></div>
          <div><div className="text-[9px] uppercase text-text-muted">Hasta</div><input type="date" value={f.hasta} onChange={(e) => setF({ ...f, hasta: e.target.value })} className="bg-app border border-border rounded px-2 py-1.5" /></div>
        </>}
        {usesPeriods && <>
          <div><div className="text-[9px] uppercase text-text-muted">Departamento</div><input value={f.departamento} onChange={(e) => setF({ ...f, departamento: e.target.value })} className="bg-app border border-border rounded px-2 py-1.5" /></div>
          <div className="min-w-[220px]"><div className="text-[9px] uppercase text-text-muted">Períodos (múltiple)</div>
            <select multiple value={selPeriods} onChange={(e) => setSelPeriods(Array.from(e.target.selectedOptions).map((o) => o.value))} className="bg-app border border-border rounded px-2 py-1 w-full h-16">
              {periods.map((p) => <option key={p.id} value={p.id}>{p.nombre} ({p.estado})</option>)}
            </select></div>
        </>}
        <button onClick={() => downloadFile(`/hr/reportes/${tipo}/export`, `rrhh_${tipo}.xlsx`, params())} data-testid="hrrep-export"
          className="ml-auto px-3 py-1.5 border border-agro-green/40 text-agro-green rounded-md flex items-center gap-1"><DownloadIcon size={14} /> Excel</button>
      </div>
      {data?.resumen && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(data.resumen).map(([k, v]) => (
            <div key={k} className="bg-card border border-border/60 rounded-lg p-3"><div className="text-[10px] uppercase text-text-muted">{k.replace(/_/g, " ")}</div><div className="font-heading text-xl text-agro-green">{v}{k.endsWith("pct") ? "%" : ""}</div></div>
          ))}
        </div>
      )}
      <div className="bg-card border border-border/60 rounded-lg overflow-x-auto">
        {!data ? <div className="p-8 text-center text-text-muted text-sm">Sin datos</div> : (
          <table className="w-full text-xs">
            <thead className="bg-sidebar"><tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              {data.columns.map((c) => <th key={c} className={`p-2 ${MONEY.has(c) ? "text-right" : ""}`}>{c.replace(/_/g, " ")}</th>)}
            </tr></thead>
            <tbody>
              {data.rows.length === 0 ? <tr><td colSpan={data.columns.length} className="p-6 text-center text-text-muted">Sin registros</td></tr>
                : data.rows.map((r, i) => (
                  <tr key={i} className="border-t border-border/30">
                    {data.columns.map((c) => <td key={c} className={`p-2 ${MONEY.has(c) ? "text-right" : ""}`}>{MONEY.has(c) ? fmtL(r[c]) : (r[c] ?? "—")}</td>)}
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Editor de tablas IHSS / RAP / INFOP / ISR (solo admin).
export function PayrollParamsEditor({ isAdmin }) {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/hr/payroll/params").then(({ data }) => setP(data)).catch(() => {}); }, []);
  if (!p) return null;
  const num = (k) => (
    <div key={k}><div className="text-[9px] uppercase text-text-muted">{k.replace(/_/g, " ")}{k.includes("pct") ? " (0–1)" : ""}</div>
      <input type="number" step="0.0001" disabled={!isAdmin} value={p[k]} onChange={(e) => setP({ ...p, [k]: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1 text-xs" data-testid={`param-${k}`} /></div>
  );
  const save = async () => {
    const body = { ...p };
    ["ihss_techo", "ihss_pct_empleado", "ihss_pct_empleador", "rap_techo", "rap_pct_empleado", "rap_pct_empleador", "infop_pct_empleador", "permisos_con_goce_anuales", "incapacidad_dias_patrono"]
      .forEach((k) => { body[k] = Number(body[k]); });
    body.isr_tramos = p.isr_tramos.map(([t, r]) => [t === "" || t === null ? null : Number(t), Number(r)]);
    try { const { data } = await api.put("/hr/payroll/params", body); setP(data); toast.success("Tablas actualizadas"); } catch (e) { toast.error(apiErr(e)); }
  };
  const setTramo = (i, j, v) => { const t = p.isr_tramos.map((x) => [...x]); t[i][j] = v; setP({ ...p, isr_tramos: t }); };
  return (
    <div className="bg-card border border-border/60 rounded-lg p-4 space-y-3" data-testid="params-editor">
      <h3 className="font-heading text-sm">Tablas IHSS / RAP / INFOP / ISR (configurables{isAdmin ? "" : " — solo admin puede editar"})</h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {["ihss_techo", "ihss_pct_empleado", "ihss_pct_empleador", "rap_techo", "rap_pct_empleado", "rap_pct_empleador", "infop_pct_empleador", "permisos_con_goce_anuales", "incapacidad_dias_patrono"].map(num)}
      </div>
      <div className="text-[10px] uppercase text-text-muted">ISR — tramos anuales (tope vacío = sin límite)</div>
      {p.isr_tramos.map(([t, r], i) => (
        <div key={i} className="grid grid-cols-3 gap-2 items-center text-xs">
          <input type="number" disabled={!isAdmin} value={t ?? ""} placeholder="∞" onChange={(e) => setTramo(i, 0, e.target.value)} className="bg-app border border-border rounded px-2 py-1" />
          <input type="number" step="0.01" disabled={!isAdmin} value={r} onChange={(e) => setTramo(i, 1, e.target.value)} className="bg-app border border-border rounded px-2 py-1" />
          {isAdmin && <button onClick={() => setP({ ...p, isr_tramos: p.isr_tramos.filter((_, x) => x !== i) })} className="text-red-400 text-left p-1.5" aria-label="Quitar tramo"><XIcon size={14} /></button>}
        </div>
      ))}
      {isAdmin && <div className="flex gap-2">
        <button onClick={() => setP({ ...p, isr_tramos: [...p.isr_tramos, ["", 0]] })} className="text-xs text-agro-green">+ Tramo</button>
        <button onClick={save} className="ml-auto px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded" data-testid="params-save">Guardar tablas</button>
      </div>}
    </div>
  );
}
